import { useRef, useState } from 'react';
import { useCuttingStore } from './state/useCuttingStore';
import { basePartId } from './lib/cutMapColors';
import { PartShape, OffcutShape } from './components/PieceShapes';
import { buildOffcutList } from './lib/offcutList';
import { isUsableOffcut, DEFAULT_MIN_OFFCUT_MM } from './lib/resultMetrics';
import { isCompletePart } from './model/partsUtils';
import { LabelsDialog } from './dialogs/LabelsDialog';
import { useSvgPanZoom } from './gestures/useSvgPanZoom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { Part } from './model/types';
import type { SheetLayout } from '@pomoshnik/shared';

/**
 * Block 14 «Раскрой» — SVG-карта раскроя (PR #22, CUT-5.2, EP-5). Read-only.
 *
 * Рендерит ТЕКУЩИЙ лист `OptimizationResult` движка: `viewBox` = габарит листа,
 * каждая размещённая деталь — `<g translate(x, svgY)>` с цветным `<rect>`, именем
 * и подписью «W × H». Порт `project.html` renderCanvas (app.js:620-653) — но карта
 * здесь read-only: drag&drop, поворот и тулбар редактирования = CUT-5.3 (не тут);
 * zoom/pan = CUT-5.2b (B-246); кромки-черточки = CUT-5.4 (B-244).
 *
 * Годные новые остатки (`freeRects`, B-245/CUT-5.4) рисуются пунктирными
 * прямоугольниками с подписью «остаток» + «W × H» — только те, у кого короткая
 * сторона ≥ порога `settings.minOffcutSize ?? 200` (`isUsableOffcut`, тот же
 * порог что у метрики #14 и кнопки «Сохранить остатки»). Мелкий отход не рисуем.
 * Заголовок карты несёт габарит листа (`sheetLength × sheetWidth`); для
 * листа-остатка (`isOffcut`) габарит листа = размер самого остатка.
 *
 * ⚠️ FLIP Y: движок даёт `y` от НИЖНЕГО края листа ((0,0) — левый-нижний угол,
 * `cutting-engine/src/types.ts:11-12`), а SVG рисует от ВЕРХНЕГО. Поэтому
 * `svgY = sheetWidth − y − h` — и для деталей, и для остатков; без флипа карта
 * вверх ногами.
 *
 * Пагер листов (несколько листов: целые + offcut-листы `isOffcut`) — локальный
 * `useState` (read-only, не в сторе): ⏮ ◀ N/M ▶ ⏭, offcut помечается «Остаток».
 * Разметка/классы — вербатим прототипа (`project.html:458-509`, `.sheet-pager`).
 *
 * 5 состояний (P-021): пустое/idle/loading/error отдаёт соседний `ResultsTab`
 * (карта рендерится sibling ПОСЛЕ него) — при `cutStatus !== 'success'` или
 * отсутствии листов CutMap возвращает `null`, чтобы не дублировать stub-карточку.
 */

/**
 * Подписи карты раскроя — АДАПТИВНЫЕ (разворот #155 «единый шрифт» обратно к
 * пропорциональному, по решению владельца). Прежний режим: один `labelFont` от
 * БОЛЬШЕЙ стороны ЛИСТА для всех кусков — давал гигантские буквы на больших
 * листах и одинаковый шрифт у крупной и мелкой детали. Теперь размер и раскладка
 * подписи зависят от габаритов КОНКРЕТНОЙ детали (`./lib/cutMapLabels`):
 *  • КРУПНАЯ — размеры вдоль кромок (длина/ширина) + имя по центру, крупный шрифт;
 *  • УЗКАЯ — повёрнутое «Д × Ш» по центру (+ имя на толстой полосе);
 *  • МЕЛКАЯ — минимальная подпись «Д × Ш».
 * Шрифт пропорционален короткой стороне детали с верхним капом `FONT_MAX`
 * («не гигантский на больших листах») и fit-guard на каждую строку. Геометрия
 * (inset кромок, повороты) тюнится визуально на превью в Chrome. Логика и пороги
 * — в `cutMapLabels.ts`; этот компонент только раскладывает <text> по дескриптору.
 */

interface CutMapCanvasProps {
  sheet: SheetLayout;
  parts: Part[];
  threshold: number;
  idx: number;
  total: number;
  titleSuffix: string;
  /**
   * Глобальное смещение нумерации годных остатков: сколько их на ПРЕДЫДУЩИХ листах
   * (`buildOffcutList(...).startBySheet[idx]`). Номер остатка на карте = `offcutStart
   * + i + 1` → совпадает с «Список остатков». По умолчанию 0.
   */
  offcutStart?: number;
  /**
   * Включить зум/пан (мобильный жест). По умолчанию `true` (экранный CutMap).
   * Печатные клоны (`CutPrintMap`) передают `false` — они `display:none` на экране
   * и не нуждаются в gesture-листенерах. Итоговое `enabled = isMobile && enablePanZoom`.
   */
  enablePanZoom?: boolean;
  /**
   * Рисовать ли линии кромок на деталях (`type1` — сплошная, иначе — пунктир). На
   * экране управляется тогглом «👁 Кромки» (`CutMap`), по умолчанию `true`. В печати и
   * PDF передаётся явно `true` (кромки в цеховой карте всегда).
   */
  showEdges?: boolean;
  /**
   * Рисовать ли линии пазов на деталях (штрих-пунктир, глубже кромки). На экране
   * управляется тогглом «Пазы» (`CutMap`), по умолчанию `true`. В печати и PDF
   * передаётся явно `true` (пазы в цеховой карте всегда).
   */
  showGrooves?: boolean;
}

/**
 * SVG-полотно одного листа. Вынесено из `CutMap`, чтобы хуки зума (`useSvgPanZoom`)
 * вызывались безусловно: компонент монтируется только в success-ветке, без
 * нарушения rules-of-hooks (в `CutMap` есть ранние `return null`). Содержимое
 * (детали + остатки) обёрнуто в `<g class="cut-map-viewport">`, на которую хук
 * пишет `transform` (зум/пан). `resetKey={idx}` сбрасывает вид при смене листа.
 *
 * Экспортируется для переиспользования печатным `CutPrintMap` (все листы на печать,
 * PR block-14-print-all-sheets) — единый рендер карты без drift подписей/FLIP-Y.
 */
export function CutMapCanvas({
  sheet,
  parts,
  threshold,
  idx,
  total,
  titleSuffix,
  offcutStart = 0,
  enablePanZoom = true,
  showEdges = true,
  showGrooves = true,
}: CutMapCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const viewportRef = useRef<SVGGElement>(null);
  const isMobile = useMediaQuery('(max-width: 480px)');
  useSvgPanZoom({
    svgRef,
    contentRef: viewportRef,
    enabled: isMobile && enablePanZoom,
    resetKey: idx,
  });

  // Номер детали = её позиция в «Список деталей» (тот же фильтр/порядок, что в
  // CutPrintDetails) → номер на карте совпадает с таблицей. Map по id детали.
  const partNumberById = new Map<string, number>(
    parts.filter(isCompletePart).map((p, i) => [p.id, i + 1]),
  );

  return (
    <svg
      ref={svgRef}
      className="sc-svg-canvas"
      viewBox={`0 0 ${sheet.sheetLength} ${sheet.sheetWidth}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Карта раскроя, лист ${idx + 1} из ${total}${titleSuffix}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <g className="cut-map-viewport" ref={viewportRef}>
        {sheet.placements.map((pl, i) => {
          const svgY = sheet.sheetWidth - pl.y - pl.h; // FLIP Y (см. шапку)
          const name = parts.find((p) => p.id === basePartId(pl.partId))?.name ?? '';
          const w = Math.round(pl.w);
          const h = Math.round(pl.h);
          const num = partNumberById.get(basePartId(pl.partId));
          // Кромки детали (чёрные на белой детали; тип различаем линией:
          // type1 — сплошная, иначе пунктир). Цвет/толщина ИНЛАЙН (не CSS-класс) —
          // корректная растеризация в PDF (svgToPng без cutting-theme).
          const edges = parts.find((p) => p.id === basePartId(pl.partId))?.edges;
          // Пазы детали (чёрные штрих-пунктирные линии, глубже кромки той же стороны;
          // тип V/U/L визуально НЕ различаем — все одинаково). Цвет/толщина ИНЛАЙН.
          const grooves = parts.find((p) => p.id === basePartId(pl.partId))?.grooves;

          return (
            <g
              key={`${pl.partId}-${i}`}
              className="part-group"
              transform={`translate(${pl.x},${svgY})`}
              role="img"
              aria-label={`Деталь ${name} ${w} × ${h}`}
            >
              <PartShape
                w={pl.w}
                h={pl.h}
                name={name}
                num={num}
                edges={edges}
                grooves={grooves}
                showEdges={showEdges}
                showGrooves={showGrooves}
              />
            </g>
          );
        })}

        {/* Годные новые остатки (B-245): пунктир + приглушённая заливка, отличны
            от деталей. Только короткая сторона ≥ порога; мелкий отход не рисуем. */}
        {sheet.freeRects
          .filter((r) => isUsableOffcut(r, threshold))
          .map((r, i) => {
            const svgY = sheet.sheetWidth - r.y - r.h; // FLIP Y (см. шапку)
            const w = Math.round(r.w);
            const h = Math.round(r.h);
            // Глобальный сквозной номер остатка (совпадает с «Список остатков»).
            const num = offcutStart + i + 1;

            return (
              <g
                key={`free-${i}`}
                className="free-rect-group"
                transform={`translate(${r.x},${svgY})`}
                role="img"
                aria-label={`Остаток ${w} × ${h}`}
              >
                <OffcutShape w={r.w} h={r.h} num={num} />
              </g>
            );
          })}
      </g>
    </svg>
  );
}

export function CutMap() {
  const lastResult = useCuttingStore((s) => s.lastResult);
  const cutStatus = useCuttingStore((s) => s.cutStatus);
  const parts = useCuttingStore((s) => s.document.parts);
  const minOffcutSize = useCuttingStore((s) => s.document.settings.minOffcutSize);

  const [sheetIndex, setSheetIndex] = useState(0);
  const [labelsOpen, setLabelsOpen] = useState(false);
  // Тогглы карты — в сторе (UI-only), чтобы печать и PDF читали то же значение.
  const mapShowEdges = useCuttingStore((s) => s.mapShowEdges);
  const setMapShowEdges = useCuttingStore((s) => s.setMapShowEdges);
  const mapShowGrooves = useCuttingStore((s) => s.mapShowGrooves);
  const setMapShowGrooves = useCuttingStore((s) => s.setMapShowGrooves);

  // Пустое/idle/loading/error закрывает ResultsTab (без дубля stub-карточки, P-021).
  if (cutStatus !== 'success' || !lastResult || lastResult.sheets.length === 0) return null;
  const sheets = lastResult.sheets;

  // Клемп индекса в render (результат мог смениться на меньшее число листов).
  const total = sheets.length;
  const idx = Math.min(Math.max(sheetIndex, 0), total - 1);
  const sheet = sheets[idx];
  if (!sheet) return null; // недостижимо (idx в пределах), но удовлетворяет noUncheckedIndexedAccess
  const go = (n: number) => setSheetIndex(Math.min(Math.max(n, 0), total - 1));

  const threshold = minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM;
  // Смещение нумерации остатков для текущего листа (сквозная нумерация ОN).
  const { startBySheet } = buildOffcutList(lastResult, threshold);

  // Габарит листа: для целого — размер листа; для листа-остатка размер листа =
  // размер самого остатка (закрывает «если из остатка — размеры остатка»).
  const sheetDims = `${Math.round(sheet.sheetLength)} × ${Math.round(sheet.sheetWidth)}`;
  const titleSuffix = sheet.isOffcut ? ` · Остаток ${sheetDims}` : ` — ${sheetDims}`;

  return (
    <div className="result-canvas-wrap" data-testid="cut-map">
      <div className="result-canvas-title">
        📐 Карта раскроя — лист {idx + 1} из {total}
        {titleSuffix}
      </div>

      <div className="result-toolbar-sc">
        <button
          type="button"
          className={`rt-btn${mapShowEdges ? ' active' : ''}`}
          title="Показать/скрыть кромки"
          onClick={() => setMapShowEdges(!mapShowEdges)}
        >
          👁 Кромки
        </button>
        <button
          type="button"
          className={`rt-btn${mapShowGrooves ? ' active' : ''}`}
          title="Показать/скрыть пазы"
          onClick={() => setMapShowGrooves(!mapShowGrooves)}
        >
          Пазы
        </button>
        <button
          type="button"
          className="rt-btn"
          title="Этикетки"
          onClick={() => setLabelsOpen(true)}
        >
          🏷 Этикетки
        </button>
      </div>

      <CutMapCanvas
        sheet={sheet}
        parts={parts}
        threshold={threshold}
        idx={idx}
        total={total}
        titleSuffix={titleSuffix}
        offcutStart={startBySheet[idx] ?? 0}
        showEdges={mapShowEdges}
        showGrooves={mapShowGrooves}
      />

      {total > 1 && (
        <div className="sheet-pager">
          <div className="pager-nav">
            <button
              type="button"
              className="btn btn-sm"
              title="Первый лист"
              aria-label="Первый лист"
              disabled={idx === 0}
              onClick={() => go(0)}
            >
              ⏮
            </button>
            <button
              type="button"
              className="btn btn-sm"
              title="Предыдущий лист"
              aria-label="Предыдущий лист"
              disabled={idx === 0}
              onClick={() => go(idx - 1)}
            >
              ◀
            </button>
            <span className="pager-info" data-testid="cut-map-pager-info">
              {idx + 1} / {total}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              title="Следующий лист"
              aria-label="Следующий лист"
              disabled={idx === total - 1}
              onClick={() => go(idx + 1)}
            >
              ▶
            </button>
            <button
              type="button"
              className="btn btn-sm"
              title="Последний лист"
              aria-label="Последний лист"
              disabled={idx === total - 1}
              onClick={() => go(total - 1)}
            >
              ⏭
            </button>
          </div>
        </div>
      )}

      {labelsOpen && <LabelsDialog onClose={() => setLabelsOpen(false)} />}
    </div>
  );
}
